import React, { useEffect, useState } from 'react';

interface ArticleFeedbackItem {
    articleId: string;
    userId: string;
    helpful: boolean;
    createdAt: string;
}

interface ArticleRequestItem {
    requestId: string;
    userId: string;
    topic: string;
    status: string;
    createdAt: string;
}

export type HelpCenterSectionProps = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

const STATUS_OPTIONS = ['Pending', 'Fulfilled', 'Declined'] as const;
const STATUS_COLORS: Record<string, string> = {
    Pending: '#f59e0b',
    Fulfilled: '#10b981',
    Declined: '#ef4444',
};

const BarRow: React.FC<{ label: string; count: number; total: number; color?: string }> = ({ label, count, total, color }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <span style={{ width: 160, fontSize: '0.85rem', color: '#444' }}>{label}</span>
        <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', height: 14 }}>
            <div
                style={{
                    width: total > 0 ? `${Math.max((count / total) * 100, count > 0 ? 2 : 0)}%` : '0%',
                    background: color || '#4f46e5',
                    height: '100%',
                }}
            />
        </div>
        <span style={{ width: 36, textAlign: 'right', fontSize: '0.85rem', fontWeight: 600 }}>{count}</span>
    </div>
);

export const HelpCenterSection: React.FC<HelpCenterSectionProps> = ({ API_BASE, authHeaders }) => {
    const [feedbackItems, setFeedbackItems] = useState<ArticleFeedbackItem[]>([]);
    const [requests, setRequests] = useState<ArticleRequestItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [requestStatuses, setRequestStatuses] = useState<Record<string, string>>({});

    const fetchAll = async () => {
        setLoading(true);
        setError(null);
        try {
            const [feedbackRes, requestsRes] = await Promise.all([
                fetch(`${API_BASE}/help-center/feedback`, { headers: authHeaders() }),
                fetch(`${API_BASE}/help-center/article-requests`, { headers: authHeaders() }),
            ]);
            if (!feedbackRes.ok) {
                const err = await feedbackRes.json().catch(() => ({}));
                throw new Error((err as any)?.message || 'Failed to fetch article feedback.');
            }
            if (!requestsRes.ok) {
                const err = await requestsRes.json().catch(() => ({}));
                throw new Error((err as any)?.message || 'Failed to fetch article requests.');
            }
            const feedbackData = await feedbackRes.json();
            const requestsData = await requestsRes.json();
            setFeedbackItems(Array.isArray(feedbackData.items) ? feedbackData.items : []);
            setRequests(Array.isArray(requestsData.items) ? requestsData.items : []);
        } catch (err: any) {
            setError(err.message || 'Error retrieving Help Center data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpdateStatus = async (requestId: string) => {
        const status = requestStatuses[requestId] || STATUS_OPTIONS[0];
        setActionLoading(true);
        setActionError(null);
        try {
            const res = await fetch(`${API_BASE}/help-center/article-requests`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ requestId, status }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error((err as any)?.message || 'Failed to update status.');
            }
            await fetchAll();
        } catch (err: any) {
            setActionError(err.message || 'Failed to update status.');
        } finally {
            setActionLoading(false);
        }
    };

    // Tally per-article yes/no counts client-side (small table, no server-side aggregation needed).
    const perArticle = new Map<string, { yes: number; no: number }>();
    for (const item of feedbackItems) {
        const bucket = perArticle.get(item.articleId) || { yes: 0, no: 0 };
        if (item.helpful) bucket.yes += 1; else bucket.no += 1;
        perArticle.set(item.articleId, bucket);
    }
    const articleRows = Array.from(perArticle.entries()).sort((a, b) => (b[1].yes + b[1].no) - (a[1].yes + a[1].no));

    const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: '#f3f4f6', fontWeight: 600, fontSize: 13, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
    const td: React.CSSProperties = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };

    return (
        <section className="table-section" style={{ maxWidth: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>Help Center</h2>
                <button
                    onClick={fetchAll}
                    disabled={loading}
                    style={{ padding: '4px 12px', fontSize: 12, cursor: 'pointer', borderRadius: 4, border: '1px solid #d1d5db', background: '#fff' }}
                >
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            {error && <p style={{ color: '#ef4444', margin: '0 0 12px' }}>{error}</p>}
            {actionError && <p style={{ color: '#ef4444', margin: '0 0 12px' }}>{actionError}</p>}

            <h3 style={{ fontSize: '1rem', marginBottom: 10 }}>Was this helpful? (by article)</h3>
            {articleRows.length === 0 && !loading && (
                <p style={{ color: '#9ca3af', fontSize: 13, marginBottom: 24 }}>No feedback yet.</p>
            )}
            {articleRows.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                    {articleRows.map(([articleId, counts]) => {
                        const total = counts.yes + counts.no;
                        return (
                            <div key={articleId} style={{ marginBottom: 12 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{articleId}</div>
                                <BarRow label="Yes" count={counts.yes} total={total} color="#10b981" />
                                <BarRow label="No" count={counts.no} total={total} color="#ef4444" />
                            </div>
                        );
                    })}
                </div>
            )}

            <h3 style={{ fontSize: '1rem', marginBottom: 10 }}>Article Requests</h3>
            {requests.length === 0 && !loading && (
                <p style={{ color: '#9ca3af', fontSize: 13 }}>No article requests yet.</p>
            )}
            {requests.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th style={th}>Requested</th>
                                <th style={th}>Topic</th>
                                <th style={th}>Status</th>
                                <th style={th}>Update Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests
                                .slice()
                                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                .map((r) => {
                                    const statusColor = STATUS_COLORS[r.status] || '#9ca3af';
                                    const selectedStatus = requestStatuses[r.requestId] || r.status;
                                    return (
                                        <tr key={r.requestId}>
                                            <td style={{ ...td, color: '#9ca3af', whiteSpace: 'nowrap' }}>{new Date(r.createdAt).toLocaleDateString()}</td>
                                            <td style={td}>{r.topic}</td>
                                            <td style={td}>
                                                <span style={{ display: 'inline-block', background: statusColor + '22', color: statusColor, borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 12 }}>
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td style={{ ...td, display: 'flex', gap: 6, alignItems: 'center' }}>
                                                <select
                                                    value={selectedStatus}
                                                    onChange={(e) => setRequestStatuses((prev) => ({ ...prev, [r.requestId]: e.target.value }))}
                                                    style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: '1px solid #d1d5db' }}
                                                >
                                                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                                <button
                                                    onClick={() => handleUpdateStatus(r.requestId)}
                                                    disabled={actionLoading}
                                                    style={{ padding: '3px 10px', fontSize: 12, cursor: 'pointer', borderRadius: 4, border: 'none', background: '#4f46e5', color: '#fff' }}
                                                >
                                                    Apply
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};
